import { ArrowRight, Check, ShieldCheck, Star } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FacePortrait } from "../components/FacePortrait";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Logo } from "../components/Logo";
import { Seo } from "../components/Seo";
import { ThemeToggle } from "../components/ThemeToggle";
import { cn } from "../lib/cn";

const HATCH =
  "repeating-linear-gradient(-45deg, rgba(255,255,255,0.7) 0, rgba(255,255,255,0.7) 1px, transparent 1px, transparent 9px)";

// Faces under the plan cards — a sample of the landing-page testimonial people.
const SOCIAL_FACES = ["Lotte Visser", "Mehmet Yıldız", "Iris Hendriks", "Daan Koster"];

// Marketing pricing page for Biedradar Pro. The catalog below MIRRORS the
// server's PLAN_LIMITS (server/src/pro/limits.ts) — keep them in sync when
// founder pricing changes. This page is public/unauthenticated, so it can't
// read the billing API; the numbers live here as static marketing copy. The
// in-app billing screen (Settings → Billing) drives the real subscription.
type Limit = number | null;

interface PlanDef {
  key: "starter" | "pro" | "scale";
  name: string;
  monthly: number;
  taglineKey: string;
  featured?: boolean;
  limits: { activeClients: Limit; newClients: Limit; uploads: Limit; signing: Limit };
}

const PLANS: PlanDef[] = [
  {
    key: "starter",
    name: "Starter",
    monthly: 49,
    taglineKey: "priceStarterTagline",
    limits: { activeClients: 25, newClients: 10, uploads: 100, signing: 20 },
  },
  {
    key: "pro",
    name: "Pro",
    monthly: 99,
    taglineKey: "priceProTagline",
    featured: true,
    limits: { activeClients: 100, newClients: 40, uploads: 500, signing: 100 },
  },
  {
    key: "scale",
    name: "Scale",
    monthly: 249,
    taglineKey: "priceScaleTagline",
    limits: { activeClients: null, newClients: null, uploads: null, signing: null },
  },
];

const INCLUDED = [
  "priceIncl1",
  "priceIncl2",
  "priceIncl3",
  "priceIncl4",
  "priceIncl5",
  "priceIncl6",
];

export function Pricing() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen overflow-x-clip bg-surface-0 font-sans text-ink">
      <Seo
        title={t("landing.priceSeoTitle")}
        description={t("landing.priceSeoDesc")}
        canonicalPath="/pricing"
      />

      {/* ── Nav ───────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-line/70 bg-surface-0/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <Link to="/" aria-label="Biedradar Pro">
            <Logo />
          </Link>
          <nav className="flex items-center gap-1">
            <Link
              to="/blog"
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:text-ink md:inline-flex"
            >
              {t("landing.navBlog")}
            </Link>
            <LanguageSwitcher />
            <ThemeToggle />
            <Link
              to="/login"
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:text-ink sm:inline-flex"
            >
              {t("landing.signIn")}
            </Link>
            <Link
              to="/start?plan=pro"
              className="ring-focus ml-1 inline-flex items-center whitespace-nowrap gap-1.5 rounded-md bg-brand px-3.5 py-1.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-brand-dim"
            >
              {t("landing.ctaPrimary")}
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero + trust bar ──────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute left-1/2 top-[-18%] -z-10 h-[55vh] w-[80vw] max-w-3xl -translate-x-1/2 rounded-full opacity-60 blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(16,118,82,0.22), transparent)" }}
        />
        <div className="mx-auto max-w-3xl px-6 pt-20 text-center sm:pt-28">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-soft">
            {t("landing.priceEyebrow")}
          </span>
          <h1 className="mt-4 font-display text-[clamp(2.4rem,6vw,4rem)] font-extrabold leading-[1.0] tracking-[-0.025em] text-ink">
            {t("landing.priceTitle")}
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-pretty text-lg leading-relaxed text-ink-muted">
            {t("landing.priceSubtitle")}
          </p>
          <ul className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm font-medium text-ink-muted">
            <TrustItem>{t("landing.priceTrial")}</TrustItem>
            <TrustItem>{t("landing.priceNoCard")}</TrustItem>
            <TrustItem>{t("landing.priceCancel")}</TrustItem>
            <TrustItem>{t("landing.priceEu")}</TrustItem>
          </ul>
        </div>
      </section>

      {/* ── Plan cards ────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pt-14">
        <div className="grid items-start gap-5 lg:grid-cols-3">
          {PLANS.map((p) => (
            <PlanCard key={p.key} plan={p} />
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-ink-muted">{t("landing.priceSeatNote")}</p>

        {/* Social proof — same people as the landing testimonials */}
        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
          <div className="flex -space-x-2.5">
            {SOCIAL_FACES.map((name) => (
              <FacePortrait key={name} name={name} size={34} className="border-2 border-surface-0" />
            ))}
          </div>
          <div className="flex flex-col items-center sm:items-start">
            <div className="flex items-center gap-0.5" aria-label="5 / 5">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} size={13} className="fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="mt-0.5 text-xs font-medium text-ink-muted">{t("landing.socialProof")}</span>
          </div>
        </div>
      </section>

      {/* ── In every plan ─────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-6 py-20 lg:py-24">
        <h2 className="text-center font-display text-3xl font-extrabold tracking-tight sm:text-[34px]">
          {t("landing.priceIncludedTitle")}
        </h2>
        <ul className="mx-auto mt-9 grid max-w-3xl gap-3 sm:grid-cols-2">
          {INCLUDED.map((k) => (
            <li
              key={k}
              className="flex items-start gap-3 rounded-xl border border-line bg-surface-2 px-4 py-3.5"
            >
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand/12">
                <Check size={13} className="text-brand-soft" strokeWidth={3} />
              </span>
              <span className="text-[15px] leading-relaxed text-ink">
                {t(`landing.${k}` as const)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Guarantee callout ─────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-6">
        <div className="flex flex-col items-start gap-4 rounded-2xl border border-brand/30 bg-brand/[0.06] p-6 sm:flex-row sm:items-center sm:gap-5 sm:p-7">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/12">
            <ShieldCheck size={20} className="text-brand-soft" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight text-ink">
              {t("landing.priceGuaranteeTitle")}
            </h2>
            <p className="mt-1 text-[15px] leading-relaxed text-ink-muted">
              {t("landing.priceGuaranteeBody")}
            </p>
          </div>
        </div>
      </section>

      {/* ── Pricing FAQ pointer ───────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-2">
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-surface-1/60 px-6 py-7 text-center">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink">
            {t("landing.priceFaqTitle")}
          </h2>
          <p className="max-w-md text-[15px] leading-relaxed text-ink-muted">
            {t("landing.priceFaqBody")}
          </p>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/#faq"
              className="inline-flex items-center whitespace-nowrap gap-1.5 text-[15px] font-semibold text-brand-soft transition-colors hover:text-brand"
            >
              {t("landing.priceFaqCta")}
              <ArrowRight size={16} />
            </Link>
            <a
              href="mailto:support@biedradar.nl"
              className="text-[15px] font-semibold text-ink-muted transition-colors hover:text-ink"
            >
              {t("landing.linkContact")}
            </a>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ───────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-24 pt-12">
        <div className="relative overflow-hidden rounded-3xl border border-brand-dim/40 bg-gradient-to-br from-brand to-brand-dim px-8 py-16 text-center shadow-pop sm:px-12">
          <div className="pointer-events-none absolute inset-0 opacity-[0.10]" style={{ backgroundImage: HATCH }} />
          <h2 className="relative font-display text-3xl font-extrabold tracking-tight text-white sm:text-[40px] sm:leading-[1.05]">
            {t("landing.priceClosingTitle")}
          </h2>
          <p className="relative mx-auto mt-4 max-w-xl text-lg leading-relaxed text-white/85">
            {t("landing.priceClosingBody")}
          </p>
          <Link
            to="/start?plan=pro"
            className="ring-focus relative mt-8 inline-flex items-center whitespace-nowrap gap-2 rounded-md bg-white px-6 py-3 text-[15px] font-semibold text-brand shadow-soft transition-transform hover:-translate-y-0.5"
          >
            {t("landing.priceClosingCta")}
            <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="border-t border-line bg-surface-1/40">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 text-xs text-ink-faint sm:flex-row">
          <span>© {new Date().getFullYear()} Biedradar · {t("landing.footerRights")}</span>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/privacy" className="transition-colors hover:text-ink">{t("landing.linkPrivacy")}</Link>
            <Link to="/terms" className="transition-colors hover:text-ink">{t("landing.linkTerms")}</Link>
            <Link to="/security" className="transition-colors hover:text-ink">{t("landing.linkSecurity")}</Link>
            <Link to="/cookies" className="transition-colors hover:text-ink">{t("landing.linkCookies")}</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/* ── Pieces ─────────────────────────────────────────────────── */

function TrustItem({ children }: { children: ReactNode }) {
  return (
    <li className="inline-flex items-center whitespace-nowrap gap-1.5">
      <Check size={15} className="text-brand-soft" strokeWidth={3} />
      {children}
    </li>
  );
}

function PlanCard({ plan }: { plan: PlanDef }) {
  const { t } = useTranslation();
  const fmtLimit = (v: Limit) => (v === null ? t("landing.priceUnlimited") : String(v));
  const rows: Array<[string, Limit]> = [
    [t("landing.priceRowActiveClients"), plan.limits.activeClients],
    [t("landing.priceRowNewClients"), plan.limits.newClients],
    [t("landing.priceRowUploads"), plan.limits.uploads],
    [t("landing.priceRowSigning"), plan.limits.signing],
  ];

  return (
    <div
      className={cn(
        "relative flex h-full flex-col rounded-2xl border bg-surface-2 p-7",
        plan.featured ? "border-brand shadow-pop lg:-mt-3 lg:pb-9 lg:pt-9" : "border-line shadow-soft",
      )}
    >
      {plan.featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-soft">
          {t("landing.priceFeatured")}
        </span>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight text-ink">{plan.name}</h3>
        {plan.featured && (
          <span className="flex items-center gap-0.5" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={12} className="fill-amber-400 text-amber-400" />
            ))}
          </span>
        )}
      </div>
      <p className="mt-1.5 text-sm text-ink-muted">{t(`landing.${plan.taglineKey}` as const)}</p>

      <div className="mt-5 flex items-baseline gap-1">
        <span className="font-display text-[44px] font-extrabold tabular-nums leading-none text-ink">
          €{plan.monthly}
        </span>
        <span className="text-sm text-ink-faint">{t("landing.pricePerMonth")}</span>
      </div>
      <p className="mt-2 text-sm font-medium text-ink">{t("landing.priceSeatsIncluded")}</p>
      <p className="text-[13px] text-ink-faint">{t("landing.pricePerSeat")}</p>

      <ul className="mt-6 flex-1 space-y-2.5 border-t border-line pt-6">
        {rows.map(([label, v]) => (
          <li key={label} className="flex items-center gap-2.5 text-[14px]">
            <Check size={15} className="shrink-0 text-brand-soft" strokeWidth={2.5} />
            <span className="text-ink-muted">
              {label}: <span className="font-semibold tabular-nums text-ink">{fmtLimit(v)}</span>
            </span>
          </li>
        ))}
      </ul>

      <Link
        to={`/start?plan=${plan.key}`}
        className={cn(
          "ring-focus mt-7 inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors",
          plan.featured
            ? "bg-brand text-white shadow-glow hover:bg-brand-dim"
            : "border border-line bg-surface-1 text-ink hover:bg-surface-3",
        )}
      >
        {t("landing.priceCta")}
        <ArrowRight size={16} />
      </Link>
    </div>
  );
}
